import { FixButton } from "@/elements-react/fix-button";
import styles from "./style.module.scss";
import { Icon } from "@/elements-react/fix-icon";
import handshake from "@/assets/handshake.svg";
import classNames from "classnames";
import { t } from "i18next";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import Modal from "@/uikit/Modal/Modal";
import { Input } from "@/uikit/Input/Input";
import label from "@/assets/label.svg";
import boxes from "@/assets/boxes.svg";
import user from "@/assets/user.svg";
import phone from "@/assets/phone.svg";
import mail from "@/assets/mail.svg";
import success from "@/assets/success-form.svg";
import { useServices } from "@/services-context";
import { CurrencyConverter } from "@/util/currency-converter";
import { LeadCreateDocument, Money } from "@/generated/graphql/b2c";
import React from "preact/compat";
import { useIsMobile } from "@/util-react/useIsMobile";
import { EMAIL_REGEX, PHONE_REGEX } from "@/util/constants.ts";

export interface SuggestPriceButtonProps {
  originalProductPrice?: Money;
  className: string;
}

export const SuggestPriceButton: React.FC<SuggestPriceButtonProps> = ({ originalProductPrice, className = "" }) => {
  const services = useServices();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const priceInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    price: "",
    quantity: "",
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({
    name: "",
    phone: "",
    email: "",
    price: "",
    quantity: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { currencySymbol, price } = originalProductPrice
    ? services.formatter.getSeparateCurrencySymbolAndPrice(originalProductPrice.amount, "full", true)
    : { currencySymbol: "", price: "" };

  const isMobile = useIsMobile();

  const isFullScreenMobileMode = isMobile && !isSuccess;

  useEffect(() => {
    if (isOpen && !isSuccess && priceInputRef.current) {
      requestAnimationFrame(() => {
        priceInputRef.current?.focus();
      });
    }
  }, [isOpen, isSuccess]);

  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
    setIsSuccess(false);
    setIsLoading(false);
    setFormData({
      name: "",
      phone: "",
      email: "",
      price: "",
      quantity: "",
    });
    setFormErrors({
      name: "",
      phone: "",
      email: "",
      price: "",
      quantity: "",
    });
  }, []);

  const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, value } = target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = useCallback(() => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = t("abns9fibu49viu4b.please-enter-full-name", "Please enter your full name");
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t("nz8vigaebr8iabrf.please-enter-phone", "Please enter your phone number");
    } else if (!PHONE_REGEX.test(formData.phone.trim())) {
      newErrors.phone = t("znvy8ierfberif48.phone-number-short", "Phone number is too short");
    }

    if (!formData.email.trim()) {
      newErrors.email = t("a94gf984arfgq4p9.please-enter-email", "Please enter your email address");
    } else if (!EMAIL_REGEX.test(formData.email.trim())) {
      newErrors.email = t("q0qp94g9qwfquw9e.invalid-email", "Please enter a valid email address");
    }

    if (!formData.price.trim()) {
      newErrors.price = t("bc7w84eifbr34ibu.enter-your-price", "Please enter your price");
    }

    if (!formData.quantity.trim()) {
      newErrors.quantity = t("cb78wivbw8eirugf.enter-quantity", "Please enter quantity");
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = async () => {
    if (isLoading) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await services.api.client.mutate({
        mutation: LeadCreateDocument,
        variables: {
          input: {
            mail: formData.email.trim(),
            formName: "suggest_price_form",
            attributes: JSON.stringify({
              suggestPrice: formData.price,
              quantity: formData.quantity,
              originalProductPrice: `${currencySymbol} ${price}`,
              productLink: window.location.href,
            }),
            name: formData.name,
            source: "",
            phone: services.formatter.phoneCleaner(formData.phone),
          },
        },
      });

      if (result?.data?.LeadCreate) {
        setIsSuccess(true);
      } else {
        console.error("Server error:", result?.errors);
      }
    } catch (error) {
      console.error("Error in send process:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderSuccess = () => {
    return (
      <div className={`${styles.mainBlock} ${!isMobile && styles.success}`}>
        <div className={`${styles.contentSuccess} ${isMobile && styles.mobile}`}>
          <div className={styles.successIcon}>
            <Icon src={success} width={144} height={144} />
          </div>
          <div>
            <div className={styles.title}>{t("by8osdirgfbvsiu9.your-offer", "Your offer has been sent")}</div>
            <div className={styles.subtitle}>{t("aiuysbdfuvaiw38h.our-manager", "Our manager will contact you soon")}</div>
          </div>
        </div>
        <FixButton className={`${classNames(styles.button, styles.success)} ${isMobile && styles.mobile}`} onClick={handleCloseModal}>
          {t("nau8sirdufb34qw9.close", "Close")}
        </FixButton>
      </div>
    );
  };

  const renderForm = () => {
    return (
      <>
        <div>
          <div className={styles.title}>{t("aabs79fiaubfrci4.suggest-your-price", "Suggest your price")}</div>
          <div className={styles.subtitle}>{t("lag47p38ab9w3irf.make-an-offer", "Make an offer — we’ll contact you soon")}</div>
        </div>
        <div className={styles.inputBlock}>
          <div className={classNames(styles.row, isMobile && styles.column)}>
            <Input
              ref={priceInputRef}
              label={t("avh89aeirufbaunr.your-price-per-unit", "Your price per unit (excl. VAT):")}
              icon={<Icon src={label} className={styles.icon} />}
              placeholder={t("avh89aeiab748fig.your-price-placeholder", "e.g. 75.00")}
              name="price"
              type="number"
              iconPosition="prefix"
              suffix={CurrencyConverter.getCurrencySymbol(services.parameters.getVisibleCurrency())}
              value={formData.price}
              onChange={changeHandler}
              customError={formErrors.price}
            />
            <Input
              label={t("zuikrbfgvueoar42.quantity", "Quantity:")}
              icon={<Icon src={boxes} className={styles.icon} />}
              name="quantity"
              iconPosition="prefix"
              placeholder="20"
              suffix="pc"
              type="number"
              step={1}
              value={formData.quantity}
              onChange={changeHandler}
              customError={formErrors.quantity}
            />
          </div>
          <div className={styles.row}>
            <Input
              label={t("0au7asdg9823ugeb.full-name", "Full name:")}
              icon={<Icon src={user} className={styles.icon} />}
              name="name"
              iconPosition="prefix"
              placeholder="John Doe"
              value={formData.name}
              onChange={changeHandler}
              customError={formErrors.name}
            />
          </div>
          <div className={styles.row}>
            <Input
              label={t("xn9riufba9sudb2a.phone", "Phone:")}
              icon={<Icon src={phone} className={styles.icon} />}
              name="phone"
              iconPosition="prefix"
              placeholder="+44 123 456 789"
              value={formData.phone}
              onChange={changeHandler}
              customError={formErrors.phone}
            />
          </div>
          <div className={styles.row}>
            <Input
              label={t("c49q8wfghq984ffb.email", "Email:")}
              icon={<Icon src={mail} className={styles.icon} />}
              name="email"
              iconPosition="prefix"
              placeholder="example@email.com"
              value={formData.email}
              onChange={changeHandler}
              customError={formErrors.email}
            />
          </div>
        </div>

        <FixButton className={styles.button} variant="primary" onClick={handleSubmit} loading={isLoading}>
          {t("ha893fhb849bqbjk.send-offer", "Send offer")}
        </FixButton>
      </>
    );
  };

  return (
    <div className={`${classNames(isMobile && styles.wrapper)} ${className}`} style={{ flexBasis: "unset", flexGrow: "1" }}>
      <FixButton className={classNames(styles.suggestButton)} prefix={<Icon src={handshake} width={14} />} onClick={() => setIsOpen(true)}>
        <span>{t("aabs79fiaubfrci4.suggest-your-price", "Suggest your price")}</span>
      </FixButton>
      <Modal isOpen={isOpen} onClose={handleCloseModal} fullScreenMobileMode={isFullScreenMobileMode}>
        <div className={styles.mainBlock}>
          {!(isMobile && isSuccess) && renderForm()}
          {isSuccess && renderSuccess()}
        </div>
      </Modal>
    </div>
  );
};
